import { ShippingModal, useShippingFees, buildPreCheckoutPositions } from '../shipping';
import type { ApiClient, IframePoolOrderItem } from '../api';
import { useCountdown } from './useCountdown';
import { RollingNumber } from './RollingNumber';
import { currencyAffix } from '../currency';

export interface RevealShippingModalProps {
  client: ApiClient;
  /** The revealed pool item — its id is sent to the fees endpoint so the
   *  quote is per-item (and will drive shipping/tax once an address is
   *  added). */
  item: IframePoolOrderItem;
  offer: NonNullable<IframePoolOrderItem['bestActiveOffer']>;
  /** Confirms shipping — hands off to the host's shipping journey. */
  onConfirm: () => void;
  /** Cancels shipping and accepts the buyback offer instead. */
  onBuyback: () => void;
  /** Close without committing — X button or backdrop click. */
  onClose: () => void;
}

/**
 * Reveal-flow adapter for the shared `ShippingModal` (Figma 703:28734).
 *
 * No delivery address exists at reveal time, so shipping fee + sales tax
 * can't be quoted yet — they render muted as "TBD at Checkout" and the Total
 * row is omitted. The flat handling fee AND the confirm-by / auto-buyback
 * warning copy both come from the backend (`getShippingFees`) so the
 * deadline and buyback % stay in sync with the operator's config; the
 * countdown runs to the real `auto_sell_at` deadline. When the address-aware
 * fees endpoint lands, shipping/tax (and a Total) will replace these
 * placeholders.
 */
export function RevealShippingModal({
  client,
  item,
  offer,
  onConfirm,
  onBuyback,
  onClose,
}: RevealShippingModalProps) {
  // `auto_sell_at` is the real backend deadline (operator's
  // `auto_sell_after_hours`); the iframe offer serializer exposes only it,
  // not `auto_sale_at`. Counting down to `autoSellAt` shows the true
  // "time left to confirm shipping" before the auto-buyback.
  const countdown = useCountdown(offer.autoSellAt);

  // Handling fee + the confirm-by warning copy are both backend-sourced
  // (the `handling` shipping service price; the note with the operator's
  // deadline + buyback %). Fetched on open; until they land the handling row
  // shows a muted placeholder and the note stays hidden.
  const { fees } = useShippingFees(client, [item.id]);
  const positions = buildPreCheckoutPositions(fees?.handlingFee ?? null);
  const note = fees?.note ?? null;
  const { prefix, suffix } = currencyAffix();

  return (
    <ShippingModal
      onClose={onClose}
      ariaLabelledBy="shipping-confirm-title"
      countdown={countdown}
      positions={positions}
      note={note}
      secondary={{
        // The price rolls in on mount — the modal opens, the number
        // settles. Figma 703:28734 caption is "Buyback · $X".
        label: (
          <>
            Accept Buyback · {prefix}
            <RollingNumber value={offer.grossPrice} />
            {suffix}
          </>
        ),
        onClick: onBuyback,
      }}
      primary={{ label: 'Confirm Shipping', onClick: onConfirm }}
    />
  );
}
